import axios from "axios";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Context } from "../../context/context";
import { Button, Card, Modal } from "../ui";
import { AlertIcon, DeviceIcon } from "../ui/icons";

const ProfileCloseAccount = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [accountCheckStatus, setAccountCheckStatus] = useState(null);
  const [showActiveDevicesModal, setShowActiveDevicesModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { dispatch } = useContext(Context);
  const navigate = useNavigate();

  /** Ask the server whether anything blocks closure. */
  const handleLoadCheckStatus = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get("/app-api/user/check-close");
      if (data.canClose) {
        setShowConfirmationModal(true);
      } else {
        setAccountCheckStatus(data);
        setShowActiveDevicesModal(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load account status.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceScheduleReset = async (deviceID) => {
    setIsLoading(true);
    try {
      await axios.post(`/app-api/device/${deviceID}/reset-schedule`);
      toast.success("Device reset successful.");
      const remaining = accountCheckStatus.devicesToCheck.filter((device) => device.id !== deviceID);
      if (remaining.length === 0) {
        setShowActiveDevicesModal(false);
        setAccountCheckStatus(null);
        setShowConfirmationModal(true);
      } else {
        setAccountCheckStatus({ ...accountCheckStatus, devicesToCheck: remaining });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset device.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAccount = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await axios.delete("/app-api/user/close-account");
      toast.success("Account deleted successfully.");
      window.sessionStorage.removeItem("user");
      dispatch({ type: "LOGOUT" });
      navigate("/signin");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className="max-w-lg border-red-200 p-5 nav:p-6">
        <div className="mb-2 flex items-center gap-2">
          <AlertIcon className="h-5 w-5 flex-shrink-0 text-red-600" />
          <p className="text-sm font-bold text-primary-900">Close account permanently</p>
        </div>
        <p className="mb-5 text-xs leading-relaxed text-gray-600">
          Your profile, device links and dose history will be deleted. Devices you own must be reset or handed to
          another owner first. This cannot be undone.
        </p>
        <Button variant="danger" onClick={handleLoadCheckStatus} loading={isLoading}>
          Close account
        </Button>
      </Card>

      {/* Devices that block closure */}
      <Modal
        isOpen={showActiveDevicesModal}
        onRequestClose={() => setShowActiveDevicesModal(false)}
        title="These devices need your attention"
        footer={
          <Button variant="ghost" size="sm" onClick={() => setShowActiveDevicesModal(false)}>
            Cancel
          </Button>
        }
      >
        <p className="mb-4 text-sm text-gray-600">
          You still own these devices. Add another user who can take over, or reset the device, before closing your
          account.
        </p>
        <div className="space-y-3">
          {accountCheckStatus?.devicesToCheck?.map((device) => (
            <div key={device.id} className="rounded-xl border border-gray-200 p-4">
              <div className="mb-3 flex items-center gap-2.5">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <DeviceIcon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-primary-900">{device.nickname || device.id}</p>
                  <p className="truncate font-mono text-[11px] text-gray-500">{device.id}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" onClick={() => navigate(`/device/${device.id}/network`)}>
                  Add a user
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDeviceScheduleReset(device.id)}
                  loading={isLoading}
                >
                  Reset device
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Final confirmation */}
      <Modal
        isOpen={showConfirmationModal}
        onRequestClose={() => setShowConfirmationModal(false)}
        title="Close your account?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowConfirmationModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleCloseAccount} loading={isLoading}>
              Confirm
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          This permanently deletes your profile and dose history. It cannot be undone.
        </p>
      </Modal>
    </>
  );
};

export default ProfileCloseAccount;
